import React, { useState } from 'react';
import { Icon } from '../Icon/Icon';
import type {
  BookingPageConfig,
  BookingStep,
  InsuranceState,
  PatientKind,
  VisitFor,
} from '../../booking/types';

/**
 * The public booking page (spec 50 §1). One responsive column, max 560px,
 * branded in the practice's colour — not the staff app's ELVA blue.
 *
 * A request is not an appointment: the flow ends at "we're calling you now",
 * never "you're booked" (unless a returning patient is booked without a call).
 */

export interface BookingPageViewProps {
  config: BookingPageConfig;
  /** `?step=` — jump to a step. */
  initialStep?: BookingStep;
  /** `?onfile=1` — returning patient with insurance on file; step 4 is skipped. */
  onFile?: boolean;
  /** `?ins=` — force an insurance state for the demo. */
  insState?: InsuranceState | undefined;
  /** `?booked=1` — step 6 in its returning-patient form. */
  booked?: boolean;
  /** `?fee=0` — run the whole flow with no reservation fee. */
  noFee?: boolean;
  /** `?closed=1` — the page when Online Booking is off. */
  closed?: boolean;
}

export function BookingPageView({
  config,
  initialStep = 1,
  onFile = false,
  insState,
  booked = false,
  noFee = false,
  closed = false,
}: BookingPageViewProps) {
  const color = config.practiceColor;
  const [step, setStep] = useState<BookingStep>(booked ? 6 : initialStep);

  // Step 1
  const [kind, setKind] = useState<PatientKind | null>(onFile ? 'returning' : null);
  const [visitFor, setVisitFor] = useState<VisitFor | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Step 2 / 3
  const [reasonId, setReasonId] = useState<string | null>(null);
  const [dayId, setDayId] = useState<string>(config.days[0]?.id ?? '');
  const [time, setTime] = useState<string | null>(null);

  const feeApplies = (k: PatientKind | null): boolean => {
    if (noFee || config.feeAmount <= 0) return false;
    if (config.feeWho === 'everyone') return true;
    return k === 'new';
  };
  const feeOnThis = feeApplies(kind);

  const reason = config.reasons.find((r) => r.id === reasonId) ?? null;
  const reasonsForKind = kind === 'new' ? config.reasons.filter((r) => r.forNew) : config.reasons;
  const day = config.days.find((d) => d.id === dayId) ?? config.days[0];

  const insuranceLine = onFile
    ? 'On file'
    : insState === 'outofnetwork'
      ? 'Guardian · out of network'
      : insState === 'innetwork' || insState === 'found'
        ? `${config.planSummary.carrier} · in network`
        : 'No insurance added';

  const setField = (k: string, v: string) => {
    setFields((p) => ({ ...p, [k]: v }));
    setErrors((p) => (p[k] ? { ...p, [k]: '' } : p));
  };

  /* Navigation — step 4 is skipped for a returning patient on file. */
  const goFrom = (s: BookingStep, dir: 1 | -1): BookingStep => {
    let next = (s + dir) as BookingStep;
    if (next === 4 && onFile) next = (4 + dir) as BookingStep;
    return Math.min(6, Math.max(1, next)) as BookingStep;
  };
  const back = () => { setStep((s) => goFrom(s, -1)); };
  const next = () => { setStep((s) => goFrom(s, 1)); };

  const validateStep1 = (): boolean => {
    const e: Record<string, string> = {};
    if (!kind) e.kind = 'This is required.';
    if (!visitFor) e.visitFor = 'This is required.';
    const need = (k: string, msg = 'This is required.') => { if (!(fields[k] ?? '').trim()) e[k] = msg; };
    if (visitFor === 'someone_else') {
      need('pFirst'); need('pLast'); need('pDob');
      need('yFirst'); need('yLast'); need('yMobile'); need('yEmail');
    } else {
      need('firstName'); need('lastName'); need('dob'); need('mobile'); need('email');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  if (closed) {
    return (
      <Shell color={color} config={config} dots={null}>
        <Card>
          <div className="text-center">
            <Dot color={color} big />
            <h1 className="m-0 mt-4 text-[20px] font-bold text-[#1C2330]">Online booking isn&rsquo;t available right now</h1>
            <p className="m-0 mt-2 text-[14px] leading-[1.6] text-[#6B7385]">
              Please call us at <strong style={{ color }}>{config.practicePhone}</strong>.
            </p>
          </div>
        </Card>
      </Shell>
    );
  }

  const dots = step <= 5 ? <Progress step={step} color={color} /> : null;

  return (
    <Shell color={color} config={config} dots={dots}>
      {step === 1 ? (
        <Card>
          <StepHead title="Book a visit" note="Fields marked * are required." />
          {feeOnThis ? (
            <FeeLine color={color}>
              {`A $${String(config.feeAmount)} reservation fee holds your time. It comes off your bill at the visit.`}
            </FeeLine>
          ) : null}

          <Field label="Have you been to Alamo Heights Dental before?" required error={errors.kind}>
            <CardChoice
              color={color}
              value={kind}
              onChange={(v) => { setKind(v); setErrors((p) => ({ ...p, kind: '' })); }}
              options={[
                { value: 'new', label: "I'm new" },
                { value: 'returning', label: "I've been here before" },
              ]}
            />
          </Field>

          <Field label="Who is this visit for?" required error={errors.visitFor}>
            <CardChoice
              color={color}
              value={visitFor}
              onChange={(v) => { setVisitFor(v); setErrors((p) => ({ ...p, visitFor: '' })); }}
              options={[
                { value: 'me', label: 'Me' },
                { value: 'someone_else', label: 'Someone else' },
              ]}
            />
          </Field>

          {visitFor === 'someone_else' ? (
            <>
              <GroupLabel>The patient</GroupLabel>
              <Text label="First name" required k="pFirst" fields={fields} errors={errors} onChange={setField} />
              <Text label="Last name" required k="pLast" fields={fields} errors={errors} onChange={setField} />
              <Text label="Date of birth" required k="pDob" placeholder="MM/DD/YYYY" fields={fields} errors={errors} onChange={setField} />
              <GroupLabel>Your details</GroupLabel>
              <Text label="Your first name" required k="yFirst" fields={fields} errors={errors} onChange={setField} />
              <Text label="Your last name" required k="yLast" fields={fields} errors={errors} onChange={setField} />
              <Text label="Mobile phone" required k="yMobile" hint="We'll call this number to confirm your request." fields={fields} errors={errors} onChange={setField} />
              <Text label="Email" required k="yEmail" fields={fields} errors={errors} onChange={setField} />
            </>
          ) : (
            <>
              <Text label="First name" required k="firstName" fields={fields} errors={errors} onChange={setField} />
              <Text label="Last name" required k="lastName" fields={fields} errors={errors} onChange={setField} />
              <Text label="Date of birth" required k="dob" placeholder="MM/DD/YYYY" fields={fields} errors={errors} onChange={setField} />
              <Text label="Mobile phone" required k="mobile" hint="We'll call this number to confirm your request." fields={fields} errors={errors} onChange={setField} />
              <Text label="Email" required k="email" fields={fields} errors={errors} onChange={setField} />
            </>
          )}

          <Primary color={color} onClick={() => { if (validateStep1()) next(); }}>Continue</Primary>
          <FootNote>
            In pain or have an emergency? Call us at <strong style={{ color }}>{config.practicePhone}</strong>.
          </FootNote>
        </Card>
      ) : null}

      {step === 2 ? (
        <Card>
          <BackLink color={color} onClick={back} />
          <StepHead title="What would you like to come in for?" />
          <div className="mt-1 flex flex-col gap-2.5">
            {reasonsForKind.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => { setReasonId(r.id); next(); }}
                className="flex items-center gap-3 rounded-[12px] border border-[#E6E9EF] bg-white px-4 py-3.5 text-left transition-colors hover:border-[#CFD5DF]"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-semibold text-[#1C2330]">{r.name}</span>
                  <span className="mt-0.5 block text-[13px] leading-[1.5] text-[#6B7385]">{r.description}</span>
                </span>
                <span className="shrink-0 rounded-full bg-[#F1F3F6] px-2.5 py-1 text-[11.5px] font-semibold text-[#6B7385]">
                  {r.minutes} min
                </span>
              </button>
            ))}
          </div>
        </Card>
      ) : null}

      {step === 3 ? (
        <Card>
          <BackLink color={color} onClick={back} />
          <StepHead title="Pick a time" sub={reason ? `${reason.name} · ${String(reason.minutes)} min` : undefined} />
          {config.days.length === 0 ? (
            <p className="m-0 text-[14px] leading-[1.6] text-[#6B7385]">
              No times are open online right now. Please call us at <strong style={{ color }}>{config.practicePhone}</strong>.
            </p>
          ) : (
            <>
              <div
                className="mb-4 flex items-center gap-2 rounded-[10px] px-3 py-2.5 text-[13px] font-semibold"
                style={{ background: '#EAF6EF', color: '#1B6B4A' }}
              >
                <Icon kind="check-circ" size={15} stroke={2} />
                First available: {config.firstAvailable}
              </div>

              {/* Day strip — chips wrap, never scroll sideways. */}
              <div className="flex flex-wrap gap-2">
                {config.days.map((d) => {
                  const on = d.id === dayId;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => { setDayId(d.id); setTime(null); }}
                      className="rounded-[10px] border px-3.5 py-2 text-[13px] font-semibold"
                      style={
                        on
                          ? { background: color, borderColor: color, color: '#fff' }
                          : { background: '#fff', borderColor: '#E6E9EF', color: '#1C2330' }
                      }
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>

              {/* Times — chips wrap. */}
              <div className="mt-4 flex flex-wrap gap-2">
                {(day?.times ?? []).map((t) => {
                  const on = t === time;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => { setTime(t); }}
                      className="min-h-[44px] rounded-[10px] border px-4 text-[14px] font-semibold"
                      style={
                        on
                          ? { background: color, borderColor: color, color: '#fff' }
                          : { background: '#fff', borderColor: '#E6E9EF', color: '#1C2330' }
                      }
                    >
                      {t}
                    </button>
                  );
                })}
              </div>

              {time ? (
                <div className="mt-4 text-[13.5px] text-[#6B7385]">{config.providerLine}</div>
              ) : null}
              {feeOnThis ? (
                <FeeLine color={color} className="mt-3">
                  {`A $${String(config.feeAmount)} reservation fee holds this time.`}
                </FeeLine>
              ) : null}

              <Primary color={color} disabled={!time} onClick={next}>Continue</Primary>
            </>
          )}
        </Card>
      ) : null}

      {step === 4 ? (
        <InsuranceStep config={config} color={color} insState={insState} onBack={back} onContinue={next} />
      ) : null}
      {step === 5 ? (
        <ReviewStep
          config={config}
          color={color}
          reason={reason}
          dayLabel={day?.label ?? ''}
          time={time}
          insuranceLine={insuranceLine}
          feeOnThis={feeOnThis}
          onBack={back}
          onSend={next}
          goStep={(s) => { setStep(s); }}
          showInsuranceChange={!onFile}
        />
      ) : null}
      {step === 6 ? <ConfirmStep config={config} color={color} booked={booked} dayLabel={day?.label ?? ''} time={time} feeOnThis={feeOnThis} /> : null}
    </Shell>
  );
}

/* ───────────────────────── shell + shared bits ───────────────────────── */

function Shell({
  color,
  config,
  dots,
  children,
}: {
  color: string;
  config: BookingPageConfig;
  dots: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <main className="min-h-screen" style={{ background: '#F3F5FB', ['--practice' as string]: color }}>
      <div className="mx-auto w-full max-w-[560px] px-4 pb-20 pt-6 sm:pt-10">
        <header className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span
              className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[13px] font-bold text-white"
              style={{ background: color }}
            >
              {config.practiceInitials}
            </span>
            <span className="text-[15px] font-semibold text-[#1C2330]">{config.practiceName}</span>
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-[9px] border border-[#E6E9EF] bg-white px-2.5 py-1.5 text-[12.5px] font-semibold text-[#6B7385]"
          >
            <Icon kind="globe" size={13} stroke={1.8} /> English
          </button>
        </header>
        {dots}
        {children}
      </div>
    </main>
  );
}

function Progress({ step, color }: { step: BookingStep; color: string }) {
  return (
    <div className="mb-5 flex items-end justify-center gap-3">
      {[1, 2, 3, 4, 5].map((n) => {
        const optional = n === 4;
        const active = n <= step;
        return (
          <div key={n} className="flex flex-col items-center gap-1">
            <span
              className="h-2.5 w-2.5 rounded-full border-2"
              style={{
                borderColor: active ? color : '#CFD5DF',
                background: optional ? 'transparent' : active ? color : 'transparent',
              }}
            />
            {optional ? <span className="text-[9px] font-semibold uppercase tracking-[0.06em] text-[#98A0B2]">Optional</span> : null}
          </div>
        );
      })}
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section
      className="rounded-[16px] border border-[#E6E9EF] bg-white p-5 sm:p-6"
      style={{ boxShadow: '0 1px 3px rgba(36,41,54,0.05)' }}
    >
      {children}
    </section>
  );
}

function StepHead({ title, note, sub }: { title: string; note?: string | undefined; sub?: string | undefined }) {
  return (
    <div className="mb-4">
      <h1 className="m-0 text-[21px] font-bold leading-[1.2] tracking-[-0.01em] text-[#1C2330]">{title}</h1>
      {sub ? <p className="m-0 mt-1 text-[13.5px] text-[#6B7385]">{sub}</p> : null}
      {note ? <p className="m-0 mt-1 text-[12.5px] text-[#98A0B2]">{note}</p> : null}
    </div>
  );
}

function FeeLine({ children, color, className }: { children: React.ReactNode; color: string; className?: string }) {
  return (
    <div
      className={`mb-4 rounded-[10px] px-3.5 py-2.5 text-[13px] leading-[1.5] font-medium ${className ?? ''}`}
      style={{ background: `${color}14`, color: '#1C2330' }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <div className="mb-2 text-[13.5px] font-semibold text-[#1C2330]">
        {label}
        {required ? <span style={{ color: '#B42318' }}> *</span> : null}
      </div>
      {children}
      {error ? <div className="mt-1.5 text-[12.5px] font-medium" style={{ color: '#B42318' }}>{error}</div> : null}
    </div>
  );
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 mt-5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#98A0B2]">{children}</div>;
}

function Text({
  label,
  k,
  required,
  placeholder,
  hint,
  fields,
  errors,
  onChange,
}: {
  label: string;
  k: string;
  required?: boolean;
  placeholder?: string;
  hint?: string;
  fields: Record<string, string>;
  errors: Record<string, string>;
  onChange: (k: string, v: string) => void;
}) {
  const err = errors[k];
  return (
    <div className="mb-3.5">
      <label className="mb-1.5 block text-[12.5px] font-semibold text-[#565E72]">
        {label}
        {required ? <span style={{ color: '#B42318' }}> *</span> : null}
      </label>
      <input
        value={fields[k] ?? ''}
        placeholder={placeholder}
        onChange={(e) => { onChange(k, e.target.value); }}
        className="min-h-[44px] w-full rounded-[10px] border bg-white px-3.5 text-[15px] text-[#1C2330] outline-none focus:border-[var(--practice)]"
        style={{ borderColor: err ? '#B42318' : '#CFD5DF' }}
      />
      {hint && !err ? <div className="mt-1 text-[12px] text-[#98A0B2]">{hint}</div> : null}
      {err ? <div className="mt-1 text-[12.5px] font-medium" style={{ color: '#B42318' }}>{err}</div> : null}
    </div>
  );
}

function CardChoice<T extends string>({
  value,
  options,
  onChange,
  color,
}: {
  value: T | null;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  color: string;
}) {
  return (
    <div className="grid grid-cols-2 gap-2.5">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => { onChange(o.value); }}
            className="min-h-[44px] rounded-[12px] border px-3 py-2.5 text-[14px] font-semibold"
            style={
              on
                ? { borderColor: color, background: `${color}12`, color: '#1C2330' }
                : { borderColor: '#E6E9EF', background: '#fff', color: '#565E72' }
            }
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Primary({
  children,
  color,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  color: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="mt-5 min-h-[48px] w-full rounded-[12px] text-[15px] font-bold text-white transition-opacity"
      style={{ background: color, opacity: disabled ? 0.45 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      {children}
    </button>
  );
}

function BackLink({ color, onClick }: { color: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-3 inline-flex items-center gap-1 text-[13px] font-semibold"
      style={{ color }}
    >
      <Icon kind="arrow-l" size={14} stroke={2} /> Back
    </button>
  );
}

function FootNote({ children }: { children: React.ReactNode }) {
  return <p className="m-0 mt-4 text-center text-[12.5px] leading-[1.5] text-[#98A0B2]">{children}</p>;
}

function Dot({ color, big }: { color: string; big?: boolean }) {
  return (
    <span
      className="mx-auto flex items-center justify-center rounded-full text-white"
      style={{ background: color, width: big ? 56 : 40, height: big ? 56 : 40 }}
    >
      <Icon kind="phone" size={big ? 24 : 18} stroke={2} />
    </span>
  );
}

/* ───────────────────────── step 4 · insurance ───────────────────────── */

type InsSub = 'collect' | 'checking' | 'found' | 'notfound' | 'timeout';

function InsuranceStep({
  config,
  color,
  insState,
  onBack,
  onContinue,
}: {
  config: BookingPageConfig;
  color: string;
  insState?: InsuranceState | undefined;
  onBack: () => void;
  onContinue: () => void;
}) {
  // Seed carrier + sub-state from the demo flag.
  const seedCarrier =
    insState === 'outofnetwork'
      ? { name: 'Guardian', network: 'out' as const }
      : insState === 'innetwork' || insState === 'checking' || insState === 'found' || insState === 'notfound'
        ? { name: config.planSummary.carrier, network: 'in' as const }
        : null;
  const seedSub: InsSub =
    insState === 'checking'
      ? 'checking'
      : insState === 'found'
        ? 'found'
        : insState === 'notfound'
          ? 'notfound'
          : insState === 'timeout'
            ? 'timeout'
            : 'collect';

  const [carrier, setCarrier] = useState<{ name: string; network: 'in' | 'out' } | null>(seedCarrier);
  const [sub, setSub] = useState<InsSub>(seedSub);
  const [search, setSearch] = useState(seedCarrier?.name ?? '');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [whose, setWhose] = useState<'mine' | 'someone'>('mine');
  const setField = (k: string, v: string) => { setFields((p) => ({ ...p, [k]: v })); };

  const check = () => {
    setSub('checking');
    window.setTimeout(() => { setSub('found'); }, 1600);
  };

  const ps = config.planSummary;

  if (sub === 'found') {
    return (
      <Card>
        <BackLink color={color} onClick={onBack} />
        <h2 className="m-0 mb-4 text-[18px] font-bold text-[#1C2330]">Your insurance</h2>
        <div className="overflow-hidden rounded-[12px] border border-[#E6E9EF]">
          <SummaryRow label="Carrier">
            <span className="flex items-center gap-2">
              {ps.carrier}
              <span
                className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                style={
                  ps.network === 'in'
                    ? { background: '#EAF6EF', color: '#1B6B4A' }
                    : { background: '#F1F3F6', color: '#6B7385' }
                }
              >
                {ps.network === 'in' ? 'In network' : 'Out of network'}
              </span>
            </span>
          </SummaryRow>
          <SummaryRow label="Plan">{ps.plan}</SummaryRow>
          <SummaryRow label="Member">{ps.member}</SummaryRow>
          <SummaryRow label="Who holds the plan">{ps.heldBy}</SummaryRow>
          <SummaryRow label="Status">{ps.status}</SummaryRow>
          <SummaryRow label="This visit" last>{ps.thisVisit}</SummaryRow>
        </div>
        <Primary color={color} onClick={onContinue}>Continue</Primary>
        <button
          type="button"
          onClick={() => { setSub('collect'); }}
          className="mt-3 block w-full text-center text-[13px] font-semibold"
          style={{ color }}
        >
          Edit my details
        </button>
      </Card>
    );
  }

  return (
    <Card>
      <BackLink color={color} onClick={onBack} />
      <div className="mb-1 flex items-center gap-2">
        <h1 className="m-0 text-[21px] font-bold leading-[1.2] tracking-[-0.01em] text-[#1C2330]">Insurance</h1>
        <span className="rounded-full bg-[#F1F3F6] px-2 py-0.5 text-[11px] font-semibold text-[#6B7385]">Optional</span>
      </div>
      <p className="m-0 mb-4 text-[13.5px] text-[#6B7385]">We&rsquo;ll check what your plan covers before your visit.</p>

      {sub === 'checking' ? (
        <>
          <div className="mt-2">
            <IndetBar color={color} />
            <p className="m-0 mt-2 text-[13px] text-[#6B7385]">Checking with {carrier?.name ?? ps.carrier}&hellip;</p>
          </div>
          <div className="pointer-events-none mt-5 opacity-50">
            <CardFields color={color} fields={fields} whose={whose} setField={setField} setWhose={setWhose} />
          </div>
        </>
      ) : (
        <>
          <label className="mb-1.5 block text-[12.5px] font-semibold text-[#565E72]">Search for your insurance</label>
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCarrier(e.target.value.trim() ? { name: e.target.value, network: 'in' } : null);
            }}
            placeholder="Delta Dental, Guardian, Cigna…"
            className="min-h-[44px] w-full rounded-[10px] border border-[#CFD5DF] bg-white px-3.5 text-[15px] text-[#1C2330] outline-none focus:border-[var(--practice)]"
          />

          {carrier ? (
            <>
              {carrier.network === 'in' ? (
                <div className="mt-3 rounded-[10px] px-3.5 py-3 text-[13.5px] font-semibold" style={{ background: '#EAF6EF', color: '#1B6B4A' }}>
                  Good news. We&rsquo;re in network with {carrier.name}.
                </div>
              ) : (
                <div className="mt-3 rounded-[10px] bg-[#F1F3F6] px-3.5 py-3 text-[13.5px] text-[#565E72]">
                  <div className="font-semibold text-[#1C2330]">We&rsquo;re not in network with {carrier.name}, but we can still see you.</div>
                  <div className="mt-1">Many plans pay part of the cost out of network.</div>
                </div>
              )}

              {sub === 'notfound' ? (
                <div className="mt-3 rounded-[10px] px-3.5 py-3 text-[13px]" style={{ background: '#FBEDEC', color: '#B42318' }}>
                  We couldn&rsquo;t find this plan. Check the member ID and the name on the card.
                </div>
              ) : null}

              <div className="mt-4">
                <CardFields color={color} fields={fields} whose={whose} setField={setField} setWhose={setWhose} />
              </div>

              {sub === 'timeout' ? (
                <>
                  <div className="mt-4 rounded-[10px] bg-[#F1F3F6] px-3.5 py-3 text-[13.5px] text-[#565E72]">
                    We&rsquo;ll check your plan before your visit.
                  </div>
                  <Primary color={color} onClick={onContinue}>Continue</Primary>
                </>
              ) : (
                <>
                  <Primary color={color} onClick={check}>Check my insurance</Primary>
                  {sub === 'notfound' ? (
                    <button type="button" onClick={onContinue} className="mt-3 block w-full text-center text-[13px] font-semibold" style={{ color }}>
                      Continue anyway
                    </button>
                  ) : null}
                </>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onContinue}
                className="mt-4 min-h-[48px] w-full rounded-[12px] border-2 bg-white text-[15px] font-bold"
                style={{ borderColor: color, color }}
              >
                I don&rsquo;t have insurance
              </button>
              <button type="button" onClick={onContinue} className="mt-3 block w-full text-center text-[13px] font-semibold" style={{ color }}>
                Skip for now
              </button>
            </>
          )}
        </>
      )}
    </Card>
  );
}

function CardFields({
  color,
  fields,
  whose,
  setField,
  setWhose,
}: {
  color: string;
  fields: Record<string, string>;
  whose: 'mine' | 'someone';
  setField: (k: string, v: string) => void;
  setWhose: (w: 'mine' | 'someone') => void;
}) {
  const noErr: Record<string, string> = {};
  return (
    <>
      <Text label="Member ID" required k="memberId" fields={fields} errors={noErr} onChange={setField} />
      <Text label="Group number (optional)" k="group" fields={fields} errors={noErr} onChange={setField} />
      <Field label="Whose plan is it?" required>
        <div className="grid grid-cols-2 gap-2.5">
          {([['mine', 'Mine'], ['someone', "Someone else's"]] as const).map(([v, l]) => {
            const on = whose === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => { setWhose(v); }}
                className="min-h-[44px] rounded-[12px] border px-3 text-[14px] font-semibold"
                style={on ? { borderColor: color, background: `${color}12`, color: '#1C2330' } : { borderColor: '#E6E9EF', background: '#fff', color: '#565E72' }}
              >
                {l}
              </button>
            );
          })}
        </div>
      </Field>
      {whose === 'someone' ? (
        <>
          <Text label="Their first name" required k="hFirst" fields={fields} errors={noErr} onChange={setField} />
          <Text label="Their last name" required k="hLast" fields={fields} errors={noErr} onChange={setField} />
          <Text label="Their date of birth" required k="hDob" placeholder="MM/DD/YYYY" fields={fields} errors={noErr} onChange={setField} />
          <Text label="They are my…" required k="hRel" fields={fields} errors={noErr} onChange={setField} />
        </>
      ) : null}
    </>
  );
}

function SummaryRow({ label, children, last }: { label: string; children: React.ReactNode; last?: boolean }) {
  return (
    <div
      className="flex items-start justify-between gap-4 px-4 py-3"
      style={last ? undefined : { borderBottom: '1px solid #EDEFF4' }}
    >
      <span className="shrink-0 text-[12.5px] font-semibold text-[#98A0B2]">{label}</span>
      <span className="text-right text-[13.5px] font-medium text-[#1C2330]">{children}</span>
    </div>
  );
}

function IndetBar({ color }: { color: string }) {
  return (
    <div className="relative h-2 w-full overflow-hidden rounded-full bg-[#E6E9EF]">
      <style>{'@keyframes obIndet{0%{left:-40%}100%{left:100%}}'}</style>
      <span
        className="absolute top-0 h-full w-[40%] rounded-full"
        style={{ background: color, animation: 'obIndet 1.1s ease-in-out infinite' }}
      />
    </div>
  );
}

/* ───────────────────────── step 5 · review ───────────────────────── */

function ReviewStep({
  config,
  color,
  reason,
  dayLabel,
  time,
  insuranceLine,
  feeOnThis,
  onBack,
  onSend,
  goStep,
  showInsuranceChange,
}: {
  config: BookingPageConfig;
  color: string;
  reason: { name: string; minutes: number } | null;
  dayLabel: string;
  time: string | null;
  insuranceLine: string;
  feeOnThis: boolean;
  onBack: () => void;
  onSend: () => void;
  goStep: (s: BookingStep) => void;
  showInsuranceChange: boolean;
}) {
  const [consent, setConsent] = useState(false);
  const when = `${dayLabel}${time ? `, ${time}` : ''}`;
  return (
    <Card>
      <BackLink color={color} onClick={onBack} />
      <StepHead title="Check your request" />

      <div className="overflow-hidden rounded-[12px] border border-[#E6E9EF]">
        <ReviewRow label={config.patientName} onChange={() => { goStep(1); }} strong />
        <ReviewRow label={reason ? `${reason.name} · ${String(reason.minutes)} min` : 'Visit'} onChange={() => { goStep(2); }} />
        <ReviewRow label={when} sub={config.providerLine} onChange={() => { goStep(3); }} />
        <ReviewRow label={config.practiceName} muted />
        <ReviewRow label={insuranceLine} onChange={showInsuranceChange ? () => { goStep(4); } : undefined} last />
      </div>

      {feeOnThis ? (
        <div className="mt-4 rounded-[12px] px-4 py-3.5" style={{ background: `${color}12` }}>
          <div className="text-[15px] font-bold text-[#1C2330]">{`$${String(config.feeAmount)} reservation fee`}</div>
          <ul className="m-0 mt-1.5 list-none space-y-1 p-0 text-[12.5px] leading-[1.5] text-[#565E72]">
            <li>It comes off your bill at the visit.</li>
            <li>If you don&rsquo;t come, we keep it as a missed-appointment fee.</li>
            <li>You&rsquo;ll pay by a secure link after our call.</li>
          </ul>
        </div>
      ) : null}

      <label className="mt-4 flex cursor-pointer items-start gap-2.5">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => { setConsent(e.target.checked); }}
          className="mt-0.5 h-[18px] w-[18px] shrink-0"
          style={{ accentColor: color }}
        />
        <span className="text-[12.5px] leading-[1.55] text-[#565E72]">
          I agree that {config.practiceName}&rsquo;s AI assistant will call me now at{' '}
          <strong className="text-[#1C2330]">{config.callbackPhone}</strong> to confirm this request, and may text and
          email me about it. Without the call, the request can&rsquo;t be confirmed.
        </span>
      </label>

      <Primary color={color} disabled={!consent} onClick={onSend}>Send my request</Primary>
      <FootNote>Keep your phone close. We&rsquo;ll call in the next minute.</FootNote>
    </Card>
  );
}

function ReviewRow({
  label,
  sub,
  onChange,
  strong,
  muted,
  last,
}: {
  label: string;
  sub?: string;
  onChange?: (() => void) | undefined;
  strong?: boolean;
  muted?: boolean;
  last?: boolean;
}) {
  return (
    <div
      className="flex items-start justify-between gap-4 px-4 py-3"
      style={last ? undefined : { borderBottom: '1px solid #EDEFF4' }}
    >
      <div className="min-w-0">
        <div
          className={
            strong
              ? 'text-[15px] font-bold text-[#1C2330]'
              : muted
                ? 'text-[13.5px] text-[#98A0B2]'
                : 'text-[13.5px] font-medium text-[#1C2330]'
          }
        >
          {label}
        </div>
        {sub ? <div className="mt-0.5 text-[12.5px] text-[#6B7385]">{sub}</div> : null}
      </div>
      {onChange ? (
        <button type="button" onClick={onChange} className="shrink-0 text-[12.5px] font-semibold" style={{ color: 'var(--practice)' }}>
          Change
        </button>
      ) : null}
    </div>
  );
}

/* ───────────────────────── step 6 · confirmation ───────────────────────── */

function ConfirmStep({
  config,
  color,
  booked,
  dayLabel,
  time,
  feeOnThis,
}: {
  config: BookingPageConfig;
  color: string;
  booked: boolean;
  dayLabel: string;
  time: string | null;
  feeOnThis: boolean;
}) {
  const when = `${dayLabel || config.firstAvailable}${dayLabel && time ? `, ${time}` : ''}`;
  if (booked) {
    return (
      <Card>
        <div className="text-center">
          <Dot color={color} big />
          <h1 className="m-0 mt-4 text-[22px] font-bold text-[#1C2330]">You&rsquo;re booked</h1>
          <p className="m-0 mt-2 text-[15px] font-semibold text-[#1C2330]">{when}</p>
          <p className="m-0 mt-2 text-[13.5px] leading-[1.6] text-[#6B7385]">
            A confirmation is on its way by text and email.
          </p>
        </div>
      </Card>
    );
  }
  return (
    <Card>
      <div className="text-center">
        <Dot color={color} big />
        <h1 className="m-0 mt-4 text-[22px] font-bold text-[#1C2330]">We&rsquo;re calling you now</h1>
        <p className="m-0 mt-3 text-[14px] leading-[1.6] text-[#565E72]">
          Answer your phone — we&rsquo;re calling <strong className="text-[#1C2330]">{config.callbackPhone}</strong> from{' '}
          <strong className="text-[#1C2330]">{config.practicePhone}</strong> to confirm this visit.
        </p>
      </div>

      <div className="mt-4 rounded-[10px] px-3.5 py-3 text-[13.5px] font-semibold" style={{ background: '#EAF6EF', color: '#1B6B4A' }}>
        {when} is held for you until we&rsquo;ve spoken.
      </div>

      {feeOnThis ? (
        <p className="m-0 mt-3 text-[13px] leading-[1.6] text-[#6B7385]">
          After the call we&rsquo;ll text and email you a link to pay the {`$${String(config.feeAmount)}`} reservation fee.
          Your visit is booked once it&rsquo;s paid.
        </p>
      ) : null}

      <p className="m-0 mt-3 text-[13px] leading-[1.6] text-[#98A0B2]">
        If we can&rsquo;t reach you, the request is cancelled and the time goes back. You can always book again, or call
        us at <strong style={{ color }}>{config.practicePhone}</strong>.
      </p>
    </Card>
  );
}

export { Shell, Card, StepHead, FeeLine, Field, Text, GroupLabel, Primary, BackLink, FootNote, Dot, Progress };
